import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import {
  readAppGuideDismissed,
  writeAppGuideDismissed,
} from "./appGuideSession";

interface AppGuideContextType {
  isOpen: boolean;
  currentSection: number;
  openGuide: (section?: number) => void;
  closeGuide: () => void;
  goToSection: (section: number) => void;
  nextSection: () => void;
  prevSection: () => void;
  hasSeenGuide: boolean;
  markAsSeen: () => void;
}

const AppGuideContext = createContext<AppGuideContextType | undefined>(
  undefined
);

export const useAppGuide = () => {
  const context = useContext(AppGuideContext);
  if (!context) {
    throw new Error("useAppGuide must be used within an AppGuideProvider");
  }
  return context;
};

interface AppGuideProviderProps {
  children: React.ReactNode;
  totalSections: number;
}

export const AppGuideProvider: React.FC<AppGuideProviderProps> = ({
  children,
  totalSections,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  // Default dismissed until the client reads sessionStorage. Safer to keep
  // the guide off during SSR/hydration than to auto-open on a stale false.
  const [hasSeenGuide, setHasSeenGuide] = useState(true);

  useEffect(() => {
    setHasSeenGuide(readAppGuideDismissed());
  }, []);

  const markAsSeen = useCallback(() => {
    setHasSeenGuide(true);
    writeAppGuideDismissed();
  }, []);

  const openGuide = useCallback((section?: number) => {
    setCurrentSection(section ?? 0);
    setIsOpen(true);
  }, []);

  const closeGuide = useCallback(() => {
    setIsOpen(false);
    markAsSeen();
  }, [markAsSeen]);

  const goToSection = useCallback((section: number) => {
    setCurrentSection(section);
  }, []);

  const nextSection = useCallback(() => {
    setCurrentSection((prev) => Math.min(prev + 1, totalSections - 1));
  }, [totalSections]);

  const prevSection = useCallback(() => {
    setCurrentSection((prev) => Math.max(prev - 1, 0));
  }, []);

  const value: AppGuideContextType = {
    isOpen,
    currentSection,
    openGuide,
    closeGuide,
    goToSection,
    nextSection,
    prevSection,
    hasSeenGuide,
    markAsSeen,
  };

  return (
    <AppGuideContext.Provider value={value}>
      {children}
    </AppGuideContext.Provider>
  );
};
